import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { TradableAreaRegistrationIssue_MissingMaster } from '@ebalance/proto/bffuser/v1/tradable_area_service_pb'
import type { TradableAreaRegistrationIssue } from '@ebalance/proto/bffuser/v1/tradable_area_service_pb'

import type { IssuerOption } from '~/components/bidding/common/bidIssuer'

export type IntradayAreaEmptyState =
  | {
      kind: 'addToBusinessAreas'
      orderableAreaCodes: AreaCode[]
    }
  | {
      kind: 'masterNotRegistered'
      issuerType: IssuerOption['issuerType']
      missingMasters: TradableAreaRegistrationIssue_MissingMaster[]
    }

const MASTER_ORDER: readonly TradableAreaRegistrationIssue_MissingMaster[] = [
  TradableAreaRegistrationIssue_MissingMaster.SUBMITTER,
  TradableAreaRegistrationIssue_MissingMaster.DEMAND_PROCUREMENT,
  TradableAreaRegistrationIssue_MissingMaster.BG,
]

/**
 * 時間前の作成導線で選べるエリアが 0 件になった理由 (AC-BIN-111)。
 *
 * JEPX 会員情報等のため登録済みだが発注できない状態は本 AC の空状態にせず、
 * AC-IJM-013 の [[JepxMemberBidWarning]] へ委ねる。取得前・取得失敗も 0 件と断定しない。
 */
export const resolveIntradayAreaEmptyState = ({
  issuer,
  businessAreaCodes,
  registrationIssues,
  jepxAreaCodes,
}: {
  issuer: IssuerOption | undefined
  businessAreaCodes: readonly AreaCode[] | undefined
  registrationIssues: readonly TradableAreaRegistrationIssue[] | undefined
  jepxAreaCodes: readonly AreaCode[]
}): IntradayAreaEmptyState | undefined => {
  if (
    issuer === undefined ||
    businessAreaCodes === undefined ||
    registrationIssues === undefined
  ) {
    return undefined
  }

  const jepxAreas = new Set(jepxAreaCodes)
  const businessAreas = new Set(businessAreaCodes)
  const registeredAreas = issuer.registeredAreas
    .map(({ area }) => area)
    .filter((area) => jepxAreas.has(area))
  if (registeredAreas.some((area) => businessAreas.has(area))) {
    return undefined
  }
  if (registeredAreas.length === 0) {
    const missing = new Set(
      registrationIssues
        .filter(
          (issue) =>
            issue.issuerType === issuer.issuerType && jepxAreas.has(issue.area),
        )
        .map(({ missingMaster }) => missingMaster),
    )
    return {
      kind: 'masterNotRegistered',
      issuerType: issuer.issuerType,
      missingMasters: MASTER_ORDER.filter((master) => missing.has(master)),
    }
  }
  if (registeredAreas.length > 0) {
    return { kind: 'addToBusinessAreas', orderableAreaCodes: registeredAreas }
  }
  return undefined
}
